import React from 'react';
import { Link } from 'react-router-dom';
import { Edit3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface AdminEditButtonProps {
  type: 'blog' | 'page';
  id: string;
  className?: string;
  variant?: 'floating' | 'inline' | 'card';
  size?: 'sm' | 'md' | 'lg';
}

const AdminEditButton: React.FC<AdminEditButtonProps> = ({ 
  type, 
  id, 
  className = '', 
  variant = 'floating',
  size = 'md'
}) => {
  const { isAuthenticated, loading } = useAuth();

  // Don't render anything if not authenticated or still loading
  if (loading || !isAuthenticated) {
    return null;
  }

  const getEditPath = () => {
    switch (type) {
      case 'blog':
        return `/admin/blog/edit/${id}`;
      case 'page':
        return `/admin/pages/edit/${id}`;
      default:
        return '/admin';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-2 py-1 text-xs';
      case 'lg':
        return 'px-4 py-3 text-base';
      default:
        return 'px-3 py-2 text-sm';
    }
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'inline':
        return 'inline-flex items-center space-x-2 bg-gray-100 hover:bg-amber-100 text-gray-600 hover:text-amber-600 px-2 py-1 rounded text-xs transition-colors';
      case 'card':
        return 'absolute top-2 right-2 bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-600 hover:text-amber-600 hover:bg-amber-50 hover:border-amber-200 px-2 py-1 rounded shadow-sm hover:shadow-md transition-all duration-200 opacity-0 group-hover:opacity-100';
      default: // floating
        return 'fixed top-24 right-4 z-40 bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-600 hover:text-amber-600 hover:bg-amber-50 hover:border-amber-200 shadow-lg hover:shadow-xl transition-all duration-200 group';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'sm':
        return 14;
      case 'lg':
        return 18;
      default:
        return 16;
    }
  };

  return (
    <Link
      to={getEditPath()}
      className={`
        ${getVariantClasses()}
        ${getSizeClasses()}
        ${className}
      `}
      title={`Edit ${type === 'blog' ? 'blog post' : 'page'}`}
    >
      <div className="flex items-center space-x-2">
        <Edit3 size={getIconSize()} className="group-hover:scale-110 transition-transform" />
        {variant !== 'card' && <span className="font-medium">Edit</span>}
      </div>
    </Link>
  );
};

export default AdminEditButton; 